import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PublicAPI, StaffAPI, apiError } from "@/lib/api";

// Peran pengurus wilayah yang bisa mendaftar mandiri + level organisasi & penyetujunya.
const ROLE_OPTIONS = [
  { value: "ADMIN_WILAYAH", label: "Admin Kantor Wilayah", orgLevel: "KANTOR_WILAYAH", approver: "Admin Pusat" },
  { value: "ADMIN_DAERAH", label: "Admin Kantor Daerah", orgLevel: "KANTOR_DAERAH", approver: "Admin Kantor Wilayah induknya" },
  { value: "ADMIN_CABANG", label: "Admin Kantor Cabang", orgLevel: "KANTOR_CABANG", approver: "Admin Kantor Daerah induknya" },
  { value: "ADMIN_RANTING", label: "Admin Kantor Ranting", orgLevel: "KANTOR_RANTING", approver: "Admin Kantor Cabang induknya" },
];

export default function RegisterStaff() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone_number: "", nik: "", password: "", requested_role: "", organization_id: "" });
  const [orgs, setOrgs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const roleInfo = ROLE_OPTIONS.find((r) => r.value === form.requested_role);

  // Muat daftar wilayah sesuai level yang relevan dengan peran yang dipilih.
  useEffect(() => {
    if (!roleInfo) return setOrgs([]);
    PublicAPI.organizations(roleInfo.orgLevel).then(setOrgs).catch(() => setOrgs([]));
  }, [roleInfo]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.phone_number || form.nik.length !== 16 || !form.password || !form.requested_role || !form.organization_id) {
      return toast.error("Lengkapi semua field: nama, NIK 16 digit, HP, sandi, peran, dan wilayah.");
    }
    setSubmitting(true);
    try {
      const res = await StaffAPI.register({ ...form, organization_id: Number(form.organization_id) });
      setDone(res);
      toast.success("Pengajuan terkirim!");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-sage/20 text-forest grid place-items-center mx-auto"><CheckCircle2 size={32} /></div>
        <h1 className="font-display text-2xl font-bold text-ink mt-4">Pengajuan Terkirim</h1>
        <p className="text-ink/70 mt-2">{done.message}</p>
        <p className="text-sm text-ink/55 mt-1">Anda akan menerima akses setelah pengajuan disetujui. Silakan cek berkala atau hubungi pengurus wilayah atasan.</p>
        <Link to="/masuk"><Button className="mt-6 rounded-full bg-forest hover:bg-forest-dark text-white">Kembali ke Halaman Masuk</Button></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-5 lg:px-0 py-10 lg:py-16">
      <div className="pill-tag bg-forest/10 text-forest mb-4"><Building2 size={13} className="inline mr-1" /> Pendaftaran Pengurus Wilayah</div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Daftar Sebagai Admin Wilayah</h1>
      <p className="text-ink/70 mt-2 text-sm">
        Akun tidak langsung aktif. Setiap pendaftaran diverifikasi berjenjang: Admin Daerah oleh Admin Pusat,
        Admin Cabang oleh Admin Daerah induknya, dan Admin Ranting oleh Admin Cabang induknya.
      </p>

      <div className="mt-8 bg-white rounded-3xl border border-stone-soft p-7 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Peran yang Diajukan</Label>
            <Select value={form.requested_role} onValueChange={(v) => setForm((f) => ({ ...f, requested_role: v, organization_id: "" }))}>
              <SelectTrigger className="rounded-lg border-stone-soft" data-testid="staff-reg-role"><SelectValue placeholder="Pilih peran..." /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Wilayah yang Diajukan</Label>
            <Select value={form.organization_id} onValueChange={set("organization_id")} disabled={!roleInfo}>
              <SelectTrigger className="rounded-lg border-stone-soft" data-testid="staff-reg-org"><SelectValue placeholder={roleInfo ? "Pilih wilayah..." : "Pilih peran dahulu"} /></SelectTrigger>
              <SelectContent>
                {orgs.length === 0 && <div className="px-3 py-2 text-xs text-ink/55">Tidak ada wilayah tersedia</div>}
                {orgs.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.name}{o.parent_name ? ` (${o.parent_name})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {roleInfo && (
          <div className="rounded-xl bg-cream p-3 text-xs text-ink/70 flex gap-2 items-start">
            <ShieldCheck size={14} className="text-forest shrink-0 mt-0.5" />
            Pengajuan ini akan diverifikasi oleh <b>{roleInfo.approver}</b>.
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nama Lengkap</Label>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} className="rounded-lg border-stone-soft" data-testid="staff-reg-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">NIK (16 digit)</Label>
            <Input value={form.nik} onChange={(e) => set("nik")(e.target.value.replace(/\D/g, ""))} maxLength={16} className="rounded-lg border-stone-soft" data-testid="staff-reg-nik" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">No. HP</Label>
            <Input value={form.phone_number} onChange={(e) => set("phone_number")(e.target.value)} className="rounded-lg border-stone-soft" data-testid="staff-reg-phone" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Email (opsional)</Label>
            <Input value={form.email} onChange={(e) => set("email")(e.target.value)} className="rounded-lg border-stone-soft" data-testid="staff-reg-email" />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Kata Sandi</Label>
            <Input type="password" value={form.password} onChange={(e) => set("password")(e.target.value)} className="rounded-lg border-stone-soft" data-testid="staff-reg-password" />
          </div>
        </div>

        <Button onClick={submit} disabled={submitting} className="w-full rounded-full bg-forest hover:bg-forest-dark text-white h-12" data-testid="staff-reg-submit">
          {submitting ? "Mengirim…" : "Ajukan Pendaftaran"} <ArrowRight size={16} />
        </Button>
        <p className="text-sm text-center text-ink/70">
          Bukan pengurus wilayah? <Link to="/daftar" className="text-forest font-semibold hover:underline">Daftar sebagai anggota</Link>
        </p>
      </div>
    </div>
  );
}
